import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Radio,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import DecimalField from '../shared/DecimalField';
import PartAutocomplete from '../shared/PartAutocomplete';
import { PartSectionHeader, PartCandidateRow } from '../shared/PartSourceSections';
import { formatPriceWithCurrency } from '../../utils/vat';

// Same sectioning as the AI work order dialog: the workshop's own register first, then priced
// catalogue and supplier rows, then knowledge-base rows nobody could price.
const SECTIONS = [
  { key: 'registry', sources: ['registry'], Icon: InventoryOutlinedIcon },
  { key: 'priced', sources: ['catalogue', 'supplier'], Icon: MenuBookOutlinedIcon },
  { key: 'kb', sources: ['advisory', 'not_found'], Icon: AutoAwesomeOutlinedIcon },
];

/**
 * Replace a Tarjousautomaatti estimate line ("Jarrupalat (arvio)") with a real part, in place.
 *
 * Suggestions come from the composition knowledge base for this vehicle and job
 * (GET /api/quote-machine/line-candidates/); any other part can be searched below them.
 * The line keeps its position, discount and job link; the price follows the add-part rules
 * (own sell price, else cost and margin). The fit label is honest about its grounds and the
 * user always chooses: nothing is swapped without a click on "Vaihda".
 */
export default function SwapEstimatePartDialog({ open, onClose, line, maintenanceId, onSwapped }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [probing, setProbing] = useState(false);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(null); // a candidate row
  const [manualPart, setManualPart] = useState(null); // a Part from the search
  const [quantity, setQuantity] = useState('1');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const fetchCandidates = async (probe) => {
    if (!line?.id) return;
    probe ? setProbing(true) : setLoading(true);
    try {
      const resp = await axios.get('/api/quote-machine/line-candidates/', {
        params: { line: line.id, probe: probe ? 1 : 0 },
      });
      setData(resp.data);
      setLoadError(null);
    } catch (error) {
      if (!probe) setData(null);
      setLoadError(error.response?.data?.error || t('quoteMachine.swap.loadError'));
    } finally {
      probe ? setProbing(false) : setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setManualPart(null);
    setSaveError(null);
    setQuantity(line?.quantity ? String(parseFloat(line.quantity)) : '1');
    fetchCandidates(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, line?.id]);

  // The operation's own part (the main choice) first, what the pool bought with it after.
  const parts = useMemo(
    () => [...(data?.parts || [])].sort((a, b) => (a.kind === 'extra' ? 1 : 0) - (b.kind === 'extra' ? 1 : 0)),
    [data]
  );
  const fit = data?.fit || null;
  const choice = manualPart ? { kind: 'part', part: manualPart } : selected ? { kind: 'candidate', candidate: selected } : null;

  const pickCandidate = (row) => {
    if (!row.swappable) return;
    setManualPart(null);
    setSelected(row);
    setQuantity(String(row.quantity || 1));
    setSaveError(null);
  };

  const pickManual = (part) => {
    if (!part) return;
    setSelected(null);
    setManualPart(part);
    setSaveError(null);
  };

  const handleSwap = async () => {
    if (!choice || saving) return;
    setSaving(true);
    setSaveError(null);
    const body = { quantity };
    if (choice.kind === 'part') {
      body.part_id = choice.part.id;
    } else {
      body.candidate = { ...choice.candidate, precision: fit?.precision || null };
    }
    try {
      const resp = await axios.patch(`/api/maintenances/${maintenanceId}/swap_part/${line.id}/`, body);
      onSwapped?.(resp.data);
      onClose();
    } catch (error) {
      setSaveError(error.response?.data?.error || t('quoteMachine.swap.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const fitChip = fit ? (
    <Tooltip title={t(`aiWorkOrder.precisionHelp.${fit.precision}`)} arrow enterTouchDelay={0}>
      <Chip
        size="small"
        data-testid="swap-fit-chip"
        color={fit.trusted ? 'success' : 'default'}
        variant={fit.trusted ? 'filled' : 'outlined'}
        icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
        label={t(`quoteMachine.fitting.precision.${fit.precision}`)}
      />
    </Tooltip>
  ) : null;

  const secondary = (row) => [row.brand, row.code, row.vendor].filter(Boolean).join(' · ') || null;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth data-testid="swap-estimate-part-dialog">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700 }}>{t('quoteMachine.swap.title')}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {line?.name}
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={saving} aria-label={t('Close')}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 3, justifyContent: 'center' }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">{t('quoteMachine.swap.loading')}</Typography>
          </Box>
        ) : (
          <>
            {loadError && <Alert severity="warning">{loadError}</Alert>}
            {data && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t('quoteMachine.swap.suggestionsFor', { job: data.name })}
                </Typography>
                {fitChip}
                {fit?.position_fallback && (
                  <Chip size="small" variant="outlined" color="warning" label={t('quoteMachine.swap.positionUnknown')} />
                )}
              </Box>
            )}
            {data && parts.length === 0 && (
              <Typography variant="body2" color="text.secondary" data-testid="swap-no-suggestions">
                {t('quoteMachine.swap.none')}
              </Typography>
            )}
            {SECTIONS.map(({ key, sources, Icon }) => {
              const rows = parts.filter((p) => sources.includes(p.source));
              if (rows.length === 0) return null;
              return (
                <Box key={key}>
                  <PartSectionHeader
                    icon={<Icon sx={{ fontSize: 16, color: 'secondary.main' }} />}
                    title={t(`aiWorkOrder.section.${key}`)}
                    help={t(`aiWorkOrder.sectionHelp.${key}`)}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {rows.map((row, i) => {
                      const isSelected = selected === row;
                      return (
                        <PartCandidateRow
                          key={`${row.kb_identity || row.code || i}-${i}`}
                          testId={`swap-candidate-${row.source}`}
                          disabled={!row.swappable}
                          onClick={() => pickCandidate(row)}
                          primary={row.name || row.code}
                          secondary={secondary(row)}
                          leading={<Radio checked={isSelected} disabled={!row.swappable} size="small" sx={{ p: 0.5 }} />}
                          trailing={
                            <>
                              {row.kind === 'extra' && (
                                <Chip size="small" variant="outlined" label={t('quoteMachine.fitting.extraChip')} data-testid="swap-extra-chip" />
                              )}
                              {row.price_flag && (
                                <Tooltip title={t('quoteMachine.fitting.priceFlagHelp')} arrow enterTouchDelay={0}>
                                  <Chip
                                    size="small"
                                    color="warning"
                                    variant="outlined"
                                    icon={<WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />}
                                    label={t(`quoteMachine.fitting.priceFlag.${row.price_flag}`)}
                                    data-testid="swap-price-flag"
                                  />
                                </Tooltip>
                              )}
                              {row.source === 'supplier' && row.price != null && (
                                <Chip size="small" variant="outlined" label={t('quoteMachine.swap.priceFromSupplier')} data-testid="swap-supplier-price-chip" />
                              )}
                              <Typography variant="body2" sx={{ minWidth: 70, textAlign: 'right', fontWeight: 600, fontSize: 13 }}>
                                {row.price != null ? formatPriceWithCurrency(row.price) : t('quoteMachine.swap.noPrice')}
                              </Typography>
                            </>
                          }
                        />
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
            {data?.can_probe && !data?.probed && parts.some((p) => !p.purchasable && p.source !== 'advisory') && (
              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={probing ? <CircularProgress size={14} /> : <LocalShippingOutlinedIcon />}
                  disabled={probing}
                  onClick={() => fetchCandidates(true)}
                  data-testid="swap-probe-suppliers"
                  sx={{ textTransform: 'none', borderRadius: '8px' }}
                >
                  {probing ? t('quoteMachine.swap.probing') : t('quoteMachine.swap.probe')}
                </Button>
              </Box>
            )}

            <Divider />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{t('quoteMachine.swap.search')}</Typography>
              <PartAutocomplete
                onSelect={pickManual}
                sourceToggle
                shelfOnlyDefault={false}
                persistKey={null}
                clearOnSelect
                inputTestId="swap-part-search"
              />
              {manualPart && (
                <Box sx={{ mt: 1 }}>
                  <PartCandidateRow
                    testId="swap-manual-choice"
                    primary={manualPart.name}
                    secondary={[manualPart.manufacturer_name, manualPart.primary_code || manualPart.manufacturer_code].filter(Boolean).join(' · ') || null}
                    leading={<Radio checked size="small" sx={{ p: 0.5 }} />}
                    trailing={
                      <Typography variant="body2" sx={{ minWidth: 70, textAlign: 'right', fontWeight: 600, fontSize: 13 }}>
                        {manualPart.sell_price != null ? formatPriceWithCurrency(manualPart.sell_price) : t('quoteMachine.swap.marginPrice')}
                      </Typography>
                    }
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <DecimalField
                label={t('quoteMachine.swap.quantity')}
                value={quantity}
                onChange={setQuantity}
                size="small"
                sx={{ width: 110 }}
                inputProps={{ 'data-testid': 'swap-quantity' }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 180 }}>
                {t('quoteMachine.swap.priceNote')}
              </Typography>
            </Box>
            {saveError && <Alert severity="error" data-testid="swap-error">{saveError}</Alert>}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          {t('Cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSwap}
          disabled={!choice || saving || loading}
          data-testid="swap-confirm"
          sx={{ textTransform: 'none', borderRadius: '8px' }}
        >
          {saving ? t('quoteMachine.swap.saving') : t('quoteMachine.swap.action')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
